import { lazy, Suspense } from 'react';
import { Check, Diamond, Hourglass, ListChecks, ListTree, MessageSquare, Paperclip, Pin, Repeat as RepeatIcon } from 'lucide-react';
import { repeatText } from './repeat.js';
import { ViewersMark } from './Presence.js';
import { dueState } from '@werft/domain';
import type { DisplayProp, LabelDto, ProjectMemberDto, TaskDto } from '@werft/contracts';
import { LabelChip, LabelDot, PriorityFlag } from './Labels.js';
import { formatDue, t, today } from '../../i18n/index.js';
import { Popover } from '../../ui/Popover.js';
import type { TaskDetailTab } from './detailTabs.js';

/** P19: Raute vor dem Titel eines Meilensteins (Kalender, Übersicht, Tabelle) */
export function MilestoneMark({ on }: { on: boolean }) {
  return on ? <Diamond className="mr-1 inline size-3.5 shrink-0 fill-[var(--color-accent)] align-[-2px] text-[var(--color-accent)]" aria-label={t('milestone.mark')} role="img" /> : null;
}

/** Briefing „Arbeitsfreude“ AF03–AF07: Angaben, die einen kleinen Arbeitsraum öffnen (getrennt geladen) */
export type MetaKind = 'subtasks' | 'checklist' | 'attachments' | 'blockers' | 'repeat' | 'milestone' | 'pin';
export type PickerKind = 'due' | 'people' | 'priority' | 'labels' | MetaKind;
/** Aufgabe öffnen, optional in einem Bereich der Seitenleiste */
export type DetailOpen = (id: string, tab?: TaskDetailTab) => void;
const META_KINDS: readonly string[] = ['subtasks', 'checklist', 'attachments', 'blockers', 'repeat', 'milestone', 'pin'];
const MetaPopover = lazy(() => import('./MetaPopovers.js'));
/** Termin, Zuständige, Priorität, Labels – die Auswahlfenster kommen getrennt geladen (Startpaket) */
const PickerPanel = lazy(() => import('./PickerPanels.js'));
/** Ansehen geht auch ohne Bearbeitungsrecht (die Arbeitsräume zeigen dann nur, ohne aktive Felder) */
export const isPreview = (k: PickerKind | null) => k === null || META_KINDS.includes(k);

/** Angabe als Einstieg: Knopf mit Namen und kleinem Hover (AF03); ohne Ziel bleibt sie reine Anzeige */
function MetaButton({ label, onClick, className, children }: { label: string; onClick?: () => void; className: string; children: React.ReactNode }) {
  if (!onClick)
    return (
      <span aria-label={label} title={label} className={`flex items-center gap-0.5 px-1 text-xs ${className}`}>
        {children}
      </span>
    );
  return (
    <button type="button" tabIndex={-1} onClick={onClick} aria-label={label} title={label} className={`flex min-h-9 items-center gap-0.5 rounded-lg px-1 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 ${className}`}>
      {children}
    </button>
  );
}

/**
 * Termin und Zuständige direkt an der Aufgabe (Paket P4, Regel R4: ohne Kalenderwerkzeug). Leere Angaben zeigen
 * nichts (Regel R2); gesetzt erscheinen sie als kleine Schaltflächen, die das Auswahlfenster öffnen. Ohne Angabe
 * öffnen Menü oder Tastenkürzel (f, z) dasselbe Fenster.
 */
export function TaskMeta({
  task,
  members,
  picker,
  onPicker,
  compact,
  hidePeople,
  labels,
  className = 'flex shrink-0 items-center',
  onOpen,
  show,
}: {
  task: TaskDto;
  members: ProjectMemberDto[];
  picker: PickerKind | null;
  onPicker: (p: PickerKind | null) => void;
  compact?: boolean;
  /** Anordnung der Angaben (Liste am Handy: eigene Zeile unter dem Titel, Audit F03) */
  className?: string;
  /** Mein Tag: dort ist ohnehin alles meins */
  hidePeople?: boolean;
  /** Labels des Projekts (fehlt in Mein Tag: dort ohne Labels) */
  labels?: LabelDto[];
  /** AF03: Angaben als Einstiege (Kommentare → Kommentarbereich, kleine Arbeitsräume); ohne: reine Anzeige */
  onOpen?: DetailOpen;
  /** W04: gewählte Angaben der Darstellung; Warnungen (überfällig, heute, wartet, Meilenstein) bleiben immer */
  show?: ReadonlySet<DisplayProp>;
}) {
  const on = (p: DisplayProp) => !show || show.has(p);
  const names = new Map(members.map((m) => [m.userId, m.name]));
  const done = !!task.completedAt;
  const state = task.dueDate && !done ? dueState(task.dueDate, today()) : null;
  const dueColor = state === 'overdue' ? 'text-red-700 dark:text-red-400 font-medium' : state === 'today' ? 'text-[var(--color-accent)] font-medium' : 'text-zinc-500';
  const anchor = (
    <span className={className} onPointerDown={(e) => e.stopPropagation()}>
      {/* Jemand anderes hat die Aufgabe gerade offen (P9) */}
      <ViewersMark projectId={task.projectId} taskId={task.id} />
      {task.onOverview && (
        <MetaButton label={t('pin.pinned')} onClick={onOpen && (() => onPicker('pin'))} className="text-zinc-400">
          <Pin className="size-3.5" aria-hidden />
        </MetaButton>
      )}
      {/* P19: Meilenstein und Wiederholung */}
      {task.milestone && (
        <MetaButton label={t('milestone.mark')} onClick={onOpen && (() => onPicker('milestone'))} className="">
          <Diamond className="size-3.5 fill-[var(--color-accent)] text-[var(--color-accent)]" aria-hidden />
        </MetaButton>
      )}
      {task.repeat && !(done && task.repeatSpawned) && (
        <MetaButton label={t('repeat.mark', { rule: repeatText(task.repeat) })} onClick={onOpen && (() => onPicker('repeat'))} className="text-zinc-400">
          <RepeatIcon className="size-3.5" aria-hidden />
        </MetaButton>
      )}
      {/* P26: wartet auf offene Vorgänger – AF06: Klick nennt sie */}
      {task.blockers > 0 && !done && (
        <MetaButton label={t('deps.waiting', { n: task.blockers })} onClick={onOpen && (() => onPicker('blockers'))} className="text-amber-700 dark:text-amber-400">
          <Hourglass className="size-3.5" aria-hidden />
          {task.blockers}
        </MetaButton>
      )}
      {/* Labels, Unteraufgaben, Checkliste, Kommentare, Anhänge, Priorität – nur, wenn es etwas gibt (Regel R2) */}
      {on('labels') && labels && task.labelIds.length > 0 && <Chips task={task} labels={labels} max={compact ? 2 : 3} onClick={() => onPicker('labels')} />}
      {on('progress') && task.subtaskCount > 0 && (
        <MetaButton label={t('subtasks.progress', { done: task.subtaskDone, n: task.subtaskCount })} onClick={onOpen && (() => onPicker('subtasks'))} className={task.subtaskDone === task.subtaskCount ? 'text-green-700 dark:text-green-400' : 'text-zinc-500'}>
          <ListTree className="size-3.5" aria-hidden />
          {task.subtaskDone}/{task.subtaskCount}
        </MetaButton>
      )}
      {on('progress') && task.checklistCount > 0 && (
        <MetaButton label={t('checklist.progress', { done: task.checklistDone, n: task.checklistCount })} onClick={onOpen && (() => onPicker('checklist'))} className={task.checklistDone === task.checklistCount ? 'text-green-700 dark:text-green-400' : 'text-zinc-500'}>
          <ListChecks className="size-3.5" aria-hidden />
          {task.checklistDone}/{task.checklistCount}
        </MetaButton>
      )}
      {/* AF07: Kommentare öffnen direkt den Kommentarbereich */}
      {on('activity') && task.commentCount > 0 && (
        <MetaButton label={t('task.comments', { n: task.commentCount })} onClick={onOpen && (() => onOpen(task.id, 'kommentare'))} className="text-zinc-500">
          <MessageSquare className="size-3.5" aria-hidden />
          {task.commentCount}
        </MetaButton>
      )}
      {on('activity') && task.attachmentCount > 0 && (
        <MetaButton label={t('task.attachments', { n: task.attachmentCount })} onClick={onOpen && (() => onPicker('attachments'))} className="text-zinc-500">
          <Paperclip className="size-3.5" aria-hidden />
          {task.attachmentCount}
        </MetaButton>
      )}
      {task.priority !== null && !done && <PriorityFlag priority={task.priority} onClick={() => onPicker('priority')} />}
      {task.dueDate && (on('due') || state === 'overdue' || state === 'today') && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => onPicker('due')}
          aria-label={t('due.label', { date: formatDue(task.dueDate) })}
          className={`min-h-9 rounded-lg px-1 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 ${dueColor}`}
        >
          {formatDue(task.dueDate)}
        </button>
      )}
      {task.assigneeIds.length > 0 && !hidePeople && on('people') && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => onPicker('people')}
          aria-label={t('people.label', { names: task.assigneeIds.map((id) => names.get(id) ?? '?').join(', ') })}
          className="flex min-h-9 items-center -space-x-1 rounded-lg px-1 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {task.assigneeIds.slice(0, compact ? 2 : 3).map((id) => (
            <Avatar key={id} name={names.get(id) ?? '?'} />
          ))}
          {task.assigneeIds.length > (compact ? 2 : 3) && <span className="pl-2 text-[10px] text-zinc-500">+{task.assigneeIds.length - (compact ? 2 : 3)}</span>}
        </button>
      )}
    </span>
  );
  // Leistung: das Auswahlfenster wird erst aufgebaut, wenn es gebraucht wird
  if (picker === null) return anchor;
  return (
    <Popover open={picker !== null} onOpenChange={(o) => !o && onPicker(null)} anchor={anchor} label={t(`picker.${picker}`)}>
      {META_KINDS.includes(picker) ? (
        <Suspense fallback={<p className="p-1 text-zinc-500">{t('common.loading')}</p>}>
          <MetaPopover kind={picker as MetaKind} task={task} onOpen={(id, tab) => (onPicker(null), (onOpen ?? (() => {}))(id, tab))} onClose={() => onPicker(null)} />
        </Suspense>
      ) : (
        <Suspense fallback={<p className="p-1 text-zinc-500">{t('common.loading')}</p>}>
          <PickerPanel kind={picker} task={task} members={members} labels={labels ?? []} onDone={() => onPicker(null)} />
        </Suspense>
      )}
    </Popover>
  );
}

function Chips({ task, labels, max, onClick }: { task: TaskDto; labels: LabelDto[]; max: number; onClick: () => void }) {
  const byId = new Map(labels.map((l) => [l.id, l]));
  const mine = task.labelIds.map((id) => byId.get(id)).filter((l): l is LabelDto => !!l);
  if (!mine.length) return null;
  const label = t('labels.on', { names: mine.map((l) => l.name).join(', ') });
  return (
    <>
      {/* Handy: nur Farbpunkte (Platz für den Titel), ab Tablet die Namen */}
      <button type="button" tabIndex={-1} onClick={onClick} aria-label={label} className="flex min-h-9 items-center gap-0.5 px-1 sm:hidden">
        {mine.slice(0, 4).map((l) => (
          <LabelDot key={l.id} color={l.color} />
        ))}
      </button>
      <span className="hidden min-w-0 items-center gap-1 px-1 sm:flex" aria-label={label}>
        {mine.slice(0, max).map((l) => (
          <LabelChip key={l.id} label={l} onClick={onClick} />
        ))}
        {mine.length > max && <span className="text-[10px] text-zinc-500">+{mine.length - max}</span>}
      </span>
    </>
  );
}

export function Avatar({ name }: { name: string }) {
  return (
    <span title={name} className="grid size-6 place-items-center rounded-full border border-white bg-zinc-200 text-[10px] font-medium dark:border-zinc-900 dark:bg-zinc-700">
      {initials(name)}
    </span>
  );
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]!.toUpperCase())
    .join('');
}
